import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { Search, MapPin, Loader2, BadgeCheck, Building2, Heart, Bookmark, Users, SlidersHorizontal, ChevronDown, X, Share2, Clock, Plus, Wallet, ArrowRight } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { Navbar } from "@/components/Navbar";
import { api, priceLabel, peso, apiError } from "@/lib/api";
import { ShareDialog } from "@/components/ShareDialog";
import { listingShareUrl } from "@/lib/share";
import { trackEvent } from "@/lib/track";
import { proProfilePath, trackProfileClick } from "@/lib/proProfile";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BuyerNeedCard } from "@/components/BuyerNeedCard";
import { EventCard } from "@/components/EventCard";
import { RecruitmentCard } from "@/components/RecruitmentCard";
import { CalendarDays, Briefcase, Home, KeyRound, BedDouble } from "lucide-react";
import { MarketFacetPicker } from "@/components/MarketFacetPicker";
import AdPlacement from "@/components/AdPlacement";
import { PropertyCover } from "@/components/AdaptiveMediaFrame";
import { MarketplaceHero, FeaturedRow, PromotedEventsRow } from "@/components/AdDeliveryModules";
import { MarketplaceFeaturedPros } from "@/components/featured/MarketplaceFeaturedPros";
import { toast } from "sonner";
import { Seo, organizationJsonLd } from "@/components/Seo";

import { PROPERTY_TYPE_GROUPS, propertyTypeLabel, SALE_TYPES, saleTypeShortLabel } from "@/lib/taxonomy";
const ANY = "__any__";
const SPOTLIGHT_AFTER = 8; // organic listings shown before the Featured Professionals spotlight

/** Inline PSGC location search (reuses /geo/search). */
export function LocationFilter({ value, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/search`, { params: { q: q.trim(), limit: 12 } });
        setResults(data.results || []); setOpen(true);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(timer.current);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (value?.name) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 h-11" data-testid="market-location-current">
        <MapPin className="h-4 w-4 text-[#0F2C59] shrink-0" />
        <span className="text-sm font-medium text-slate-700 truncate">{value.name}</span>
        <button type="button" data-testid="market-location-clear" onClick={() => onPick({ code: "", name: "" })} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Clear</button>
      </div>
    );
  }
  return (
    <div ref={boxRef} className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        data-testid="market-location-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder="Search location…"
        className="w-full h-11 pl-9 pr-9 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2C59]/25"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="market-location-results">
          {results.map((r) => (
            <li key={r.code}>
              <button
                type="button"
                data-testid={`market-loc-${r.code}`}
                onClick={() => { onPick({ code: r.code, name: r.name }); setQ(""); setResults([]); setOpen(false); }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50"
              >
                <span className="truncate">{r.name}{r.parentName ? <span className="text-slate-400 font-normal">, {r.parentName}</span> : null}</span>
                <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{r.level === "CITY_MUNICIPALITY" ? "City" : r.level === "PROVINCE" ? "Province" : "Region"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function specLine(p) {
  const parts = [];
  if (p.bedrooms > 0) parts.push(`${p.bedrooms} Bed${p.bedrooms > 1 ? "s" : ""}`);
  if (p.bathrooms > 0) parts.push(`${p.bathrooms} Bath${p.bathrooms > 1 ? "s" : ""}`);
  if (p.parking_slots > 0) parts.push(`${p.parking_slots} Parking`);
  if (p.floor_area_sqm > 0) parts.push(`${p.floor_area_sqm} sqm`);
  return parts.join(" · ");
}

// Listing freshness using reliable timestamps — "Updated" when edited after posting, else "Posted".
function freshnessLabel(p) {
  const upd = p.updated_at, cre = p.created_at;
  const iso = upd || cre;
  if (!iso) return "";
  const verb = upd && cre && upd !== cre ? "Updated" : "Posted";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (Number.isNaN(days)) return "";
  if (days <= 0) return `${verb} today`;
  if (days === 1) return `${verb} yesterday`;
  if (days < 30) return `${verb} ${days} days ago`;
  const mo = Math.floor(days / 30);
  return `${verb} ${mo} month${mo > 1 ? "s" : ""} ago`;
}

// Marketplace thumbnail crop focus. Presentation-only (never mutates source media).
// Applies ONLY when a photo is shown with object-fit: cover (landscape / normal /
// mildly-portrait sources). Future-ready: honors an optional per-listing
// `thumbnail_focal_point` — either a CSS object-position string ("50% 40%") or an
// {x,y} percentage object — when a user later sets one. Default is a clean
// slightly-upper-center crop that keeps building subjects in frame.
function cardFocalPoint(p) {
  const f = p?.thumbnail_focal_point;
  if (typeof f === "string" && f.trim()) return f.trim();
  if (f && typeof f === "object" && (f.x != null || f.y != null)) return `${f.x ?? 50}% ${f.y ?? 50}%`;
  return "center 38%";
}

export function ListingCard({ p, showSave, saved, onToggleSave, onShare }) {
  const nav = useNavigate();
  const rent = p.listing_type === "For Rent";
  const hosting = p.listing_type === "Unit Hosting";
  const specs = specLine(p);
  const fresh = freshnessLabel(p);
  const monthlyDp = !rent && !hosting && p.payment_promo && p.payment_promo.monthly_downpayment_amount > 0
    ? p.payment_promo.monthly_downpayment_amount : null;
  return (
    <Link
      to={`/listing/${p.id}`}
      data-testid={`listing-card-${p.id}`}
      className="group self-start flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#0F2C59] hover:shadow-lg transition-all duration-300"
    >
      <PropertyCover listing={p} aspect="4/3" hoverZoom desktopCover />
      <div className="p-5 flex flex-col">
        {/* Badges + Save/Share live in the content strip — NEVER over the artwork. */}
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <div data-testid={`listing-badges-${p.id}`} className="flex flex-wrap items-center gap-1.5 min-w-0">
            <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${hosting ? "bg-emerald-600 text-white" : rent ? "bg-[#0F2C59] text-white" : "bg-[#C79A3B] text-white"}`}>
              {hosting ? "Stay" : rent ? "For Rent" : "For Sale"}
            </span>
            {!rent && !hosting && p.sale_type && (
              <span data-testid={`listing-sale-type-${p.id}`} className="text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                {saleTypeShortLabel(p.sale_type)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {showSave && (
              <button
                type="button"
                data-testid={`save-toggle-${p.id}`}
                aria-pressed={saved}
                title={saved ? "Remove from saved" : "Save property"}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave(p.id); }}
                className={`h-8 w-8 rounded-full flex items-center justify-center border transition-colors ${saved ? "bg-[#C79A3B] border-[#C79A3B] text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-[#0F2C59]"}`}
              >
                <Heart className={`h-4 w-4 ${saved ? "fill-current" : ""}`} />
              </button>
            )}
            <button
              type="button"
              data-testid={`share-toggle-${p.id}`}
              title="Share property"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare(p); }}
              className="h-8 w-8 rounded-full flex items-center justify-center border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-[#0F2C59] transition-colors"
            >
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="font-display text-xl font-bold text-slate-900">{hosting ? `₱${(p.nightly_rate || 0).toLocaleString()} / night` : priceLabel(p)}</div>
        {monthlyDp && (
          <div data-testid={`listing-monthly-dp-${p.id}`} className="inline-flex items-center gap-1.5 mt-1 text-[13px] font-semibold text-[#C79A3B]">
            <Wallet className="h-3.5 w-3.5" /> Monthly DP {peso(monthlyDp)}
          </div>
        )}
        <h3 className="text-sm font-semibold text-slate-800 mt-1.5 line-clamp-2 min-h-[2.5rem]">{p.title}</h3>
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{p.city || "Philippines"}{p.project ? ` · ${p.project}` : ""}</span>
        </p>
        <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">{propertyTypeLabel(p.property_type)}</p>
        {specs && <p className="text-xs text-slate-600 mt-3 pt-3 border-t border-slate-100">{specs}</p>}
        <div>
          {p.owner && (() => {
            const path = proProfilePath(p.owner);
            const idBody = (
              <>
                <img src={p.owner.avatar} alt="" className="h-6 w-6 rounded-full bg-slate-100 shrink-0" />
                <span className={`text-xs truncate ${path ? "text-slate-700 group-hover/pro:text-[#0F2C59] group-hover/pro:underline" : "text-slate-600"}`}>{p.owner.name}</span>
                {p.owner.verification_status === "PROFESSIONAL_VERIFIED" && <BadgeCheck className="h-3.5 w-3.5 text-success shrink-0" title="Verified professional" />}
              </>
            );
            return (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                {path ? (
                  <button
                    type="button"
                    data-testid={`listing-pro-link-${p.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); trackProfileClick(p.owner, "property_card"); nav(path); }}
                    aria-label={`View ${p.owner.name}'s professional profile`}
                    className="group/pro flex items-center gap-2 min-w-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B]/60"
                  >
                    {idBody}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 min-w-0" data-testid={`listing-pro-static-${p.id}`}>{idBody}</div>
                )}
                {fresh && <span className="ml-auto text-[11px] text-slate-400 shrink-0 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fresh}</span>}
              </div>
            );
          })()}
          {!p.owner && fresh && (
            <p className="text-[11px] text-slate-400 mt-3 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fresh}</p>
          )}
        </div>
      </div>
    </Link>
  );
}


// Guest / non-professional access gate for Buyer Needs / Requirements. The actual demand
// inventory is a protected professional-network capability (also enforced server-side).
function BuyerNeedsGate() {
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-6 pb-4" data-testid="buyer-needs-gate">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="grid md:grid-cols-5">
          <div className="md:col-span-3 p-8 sm:p-10">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C79A3B]"><Users className="h-4 w-4" /> Professional Network Access</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0F2C59] mt-3">Discover Active Buyer Needs</h2>
            <p className="text-slate-600 mt-3 max-w-xl">See what verified real estate professionals and their clients are actively looking for—and discover opportunities to match your properties.</p>
            <p className="text-slate-500 text-sm mt-4 font-medium">Buyer Needs is available to registered PropertyConnect users.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/register" data-testid="bn-gate-register" className="h-11 px-6 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold inline-flex items-center transition-colors">Create Free Account</Link>
              <Link to="/login" data-testid="bn-gate-signin" className="h-11 px-6 rounded-xl border border-[#0F2C59] text-[#0F2C59] font-semibold inline-flex items-center hover:bg-[#0F2C59]/5 transition-colors">Sign In</Link>
            </div>
            <p className="text-xs text-slate-400 mt-5">Connect with professionals. Match properties with active demand. Create more opportunities.</p>
          </div>
          <div className="md:col-span-2 bg-[#0F2C59] p-8 sm:p-10 flex flex-col justify-center gap-1 text-white">
            {[["Buyer Demand", "Active client requirements", Users], ["Smart Matching", "Your listings, their needs", ArrowRight], ["Property Opportunity", "Close more deals", Building2]].map(([t, s, Icon], i) => (
              <React.Fragment key={t}>
                {i > 0 && <div className="ml-4 border-l border-white/15 h-4" />}
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#C79A3B]" /></div>
                  <div><p className="text-sm font-semibold">{t}</p><p className="text-white/50 text-xs">{s}</p></div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Marketplace() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const locp = useLocation();
  const { user, config } = useAuth();
  const isConsumer = user && (user.account_type || "PROFESSIONAL") === "CONSUMER";
  const isPro = user && (user.account_type || "PROFESSIONAL") !== "CONSUMER";
  const verifiedPro = isPro && user?.verification_status === "PROFESSIONAL_VERIFIED";
  const recruitmentEnabled = !!config?.recruitment_enabled;
  const showSave = !isPro; // consumers + anonymous see the save affordance; professionals do not
  const [shareItem, setShareItem] = useState(null); // property being shared (ShareDialog)

  const [listingType, setListingType] = useState(["For Rent", "Unit Hosting"].includes(sp.get("type")) ? sp.get("type") : "For Sale");
  const [loc, setLoc] = useState({ code: sp.get("location_code") || "", name: sp.get("loc") || "" });
  const [propType, setPropType] = useState(sp.get("property_type") || ANY);
  const [saleType, setSaleType] = useState(sp.get("sale_type") || ANY);
  const [minPrice, setMinPrice] = useState(sp.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(sp.get("max_price") || "");
  const [guests, setGuests] = useState(sp.get("guests") || "");
  const [dev, setDev] = useState({ id: sp.get("developer_id") || null, name: sp.get("developer") || null });
  const [proj, setProj] = useState({ id: sp.get("project_id") || null, name: sp.get("project") || null });
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);
  const PAGE_SIZE = 24;
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState(sp.get("sort") || "newest");
  const [showAllFilters, setShowAllFilters] = useState(false);
  const reqSeq = useRef(0);
  const facetInited = useRef(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [propReloadKey, setPropReloadKey] = useState(0);
  const hosting = listingType === "Unit Hosting";

  // P1: Buyer Needs discovery mode
  const [mode, setMode] = useState(["buyer-needs", "events", "recruitment"].includes(sp.get("mode")) ? sp.get("mode") : "properties");
  const [bnLoc, setBnLoc] = useState({ code: "", name: "" });
  const [bnPropType, setBnPropType] = useState(ANY);
  const [bnListingType, setBnListingType] = useState(ANY);
  const [bnMinBudget, setBnMinBudget] = useState("");
  const [bnMaxBudget, setBnMaxBudget] = useState("");
  const [bnBedrooms, setBnBedrooms] = useState(ANY);
  const [bnGuests, setBnGuests] = useState(ANY);
  const [bnMinNightly, setBnMinNightly] = useState("");
  const [bnMaxNightly, setBnMaxNightly] = useState("");
  const [bnMinBeds, setBnMinBeds] = useState(ANY);
  const bnIsStay = bnListingType === "Unit Hosting";
  const [bnItems, setBnItems] = useState(null);
  const [bnTotal, setBnTotal] = useState(0);
  const [bnHasMore, setBnHasMore] = useState(false);
  const [bnLoadingMore, setBnLoadingMore] = useState(false);
  const bnPageRef = useRef(1);
  const [bnLoading, setBnLoading] = useState(false);
  const [bnSort, setBnSort] = useState("newest");
  const [bnReloadKey, setBnReloadKey] = useState(0);

  // Events discovery mode (public sees PUBLIC+PUBLISHED; authed sees PUBLIC+NETWORK_ONLY).
  const [evItems, setEvItems] = useState(null);
  const [evLoading, setEvLoading] = useState(false);
  const [evType, setEvType] = useState(ANY);
  const loadEvents = async () => {
    setEvLoading(true);
    try {
      const params = {};
      if (evType !== ANY) params.event_type = evType;
      const { data } = await api.get(user ? "/events" : "/public/events", { params });
      setEvItems(data);
    } catch { setEvItems([]); } finally { setEvLoading(false); }
  };

  // Recruitment discovery mode (public sees PUBLIC+PUBLISHED; authed pros also see NETWORK_ONLY).
  const [rcItems, setRcItems] = useState(null);
  const [rcLoading, setRcLoading] = useState(false);
  const [rcType, setRcType] = useState(ANY);
  const loadRecruitment = async () => {
    setRcLoading(true);
    try {
      const params = {};
      if (rcType !== ANY) params.employment_type = rcType;
      const { data } = await api.get(user ? "/recruitment" : "/public/recruitment", { params });
      setRcItems(data.items || []);
    } catch { setRcItems([]); } finally { setRcLoading(false); }
  };

  const bnBuildParams = () => {
    const params = {};
    if (bnLoc.code) params.location_code = bnLoc.code; else if (bnLoc.name) params.city = bnLoc.name;
    if (bnPropType !== ANY) params.property_type = bnPropType;
    if (bnListingType !== ANY) params.listing_type = bnListingType;
    if (bnIsStay) {
      if (bnMinNightly) params.min_nightly = Number(bnMinNightly);
      if (bnMaxNightly) params.max_nightly = Number(bnMaxNightly);
      if (bnGuests !== ANY) params.guests = Number(bnGuests);
      if (bnMinBeds !== ANY) params.min_beds = Number(bnMinBeds);
    } else {
      if (bnMinBudget) params.min_budget = Number(bnMinBudget);
      if (bnMaxBudget) params.max_budget = Number(bnMaxBudget);
      if (bnBedrooms !== ANY) params.bedrooms = Number(bnBedrooms);
    }
    return params;
  };

  const loadBuyerNeeds = async () => {
    if (!isPro) { setBnItems([]); return; }  // protected: professionals only (server-enforced)
    setBnLoading(true);
    const params = { ...bnBuildParams(), page: 1, page_size: 24 };
    try {
      const { data } = await api.get("/public/buyer-needs", { params });
      bnPageRef.current = 1;
      setBnItems(data.items || []);
      setBnTotal(data.total ?? (data.items || []).length);
      setBnHasMore(!!data.has_more);
    } catch { setBnItems([]); setBnTotal(0); setBnHasMore(false); } finally { setBnLoading(false); }
  };

  const loadMoreBuyerNeeds = async () => {
    if (bnLoadingMore) return;
    setBnLoadingMore(true);
    const nextPage = bnPageRef.current + 1;
    try {
      const { data } = await api.get("/public/buyer-needs", { params: { ...bnBuildParams(), page: nextPage, page_size: 24 } });
      bnPageRef.current = nextPage;
      setBnItems((prev) => [...(prev || []), ...(data.items || [])]);
      setBnHasMore(!!data.has_more);
    } catch { /* keep existing list on failure */ } finally { setBnLoadingMore(false); }
  };

  const switchMode = (m) => {
    setMode(m);
    const next = new URLSearchParams(sp); if (m !== "properties") next.set("mode", m); else next.delete("mode"); setSp(next, { replace: true });
  };

  // Keep the active Marketplace category visible in the mobile horizontal rail
  // (presentation-only: center the active tab without scrolling the page vertically).
  // Depends on recruitmentEnabled too, since that tile mounts after config resolves.
  const categoryRailRef = useRef(null);
  const activeCategoryRef = useRef(null);
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const rail = categoryRailRef.current, btn = activeCategoryRef.current;
      if (!rail || !btn) return;
      const target = btn.offsetLeft - (rail.clientWidth - btn.offsetWidth) / 2;
      rail.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [mode, recruitmentEnabled]);

  // Load buyer needs once the tab is active AND the viewer's professional status is known
  // (guards a race where a direct load/refresh on the buyer-needs tab fired before `user`
  // resolved, leaving the list permanently empty).
  useEffect(() => { if (mode === "buyer-needs" && isPro && bnItems === null) loadBuyerNeeds(); /* eslint-disable-next-line */ }, [mode, isPro]);
  // Reload buyer needs after chip removal / clear-all commits fresh filter state.
  useEffect(() => { if (bnReloadKey > 0) loadBuyerNeeds(); /* eslint-disable-next-line */ }, [bnReloadKey]);
  useEffect(() => { if (mode === "events") loadEvents(); /* eslint-disable-next-line */ }, [mode, evType, user]);
  useEffect(() => { if (mode === "recruitment") loadRecruitment(); /* eslint-disable-next-line */ }, [mode, rcType, user]);

  const buildListingParams = () => {
    const params = { listing_type: listingType };
    if (loc.code) { params.location_code = loc.code; if (loc.name) params.city = loc.name; }
    else if (loc.name) params.city = loc.name;
    if (propType !== ANY) params.property_type = propType;
    if (listingType === "For Sale" && saleType !== ANY) params.sale_type = saleType;
    if (dev.id) params.developer_id = dev.id;
    if (proj.id) params.project_id = proj.id;
    if (sort && sort !== "newest") params.sort = sort;
    if (hosting) {
      if (minPrice) params.min_nightly_rate = Number(minPrice);
      if (maxPrice) params.max_nightly_rate = Number(maxPrice);
      if (guests) params.guests = Number(guests);
    } else {
      if (minPrice) params.min_price = Number(minPrice);
      if (maxPrice) params.max_price = Number(maxPrice);
    }
    return params;
  };

  // Stable param reference for the SEPARATE sponsored modules (hero/featured). They refetch
  // ONLY when an actual filter VALUE changes — never on unrelated Marketplace re-renders
  // (save toggles, share dialog, scroll), so the organic grid is never disturbed.
  const sponsoredParams = useMemo(
    () => buildListingParams(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listingType, loc.code, loc.name, propType, saleType, dev.id, proj.id, sort, hosting, minPrice, maxPrice, guests]
  );

  // Featured Professionals P1C: contextual spotlight (location, intent, property/sale type).
  const proSpotlight = mode === "properties" ? (
    <MarketplaceFeaturedPros city={loc.name || ""} locationCode={loc.code || ""} listingType={listingType}
      propertyType={propType !== ANY ? propType : ""} saleType={listingType === "For Sale" && saleType !== ANY ? saleType : ""} />
  ) : null;

  const load = async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    const params = { ...buildListingParams(), page: 1, page_size: PAGE_SIZE };
    try {
      const { data } = await api.get("/public/listings", { params });
      if (seq === reqSeq.current) {
        pageRef.current = 1;
        setItems(data.items || []);
        setTotal(data.total ?? (data.items || []).length);
        setHasMore(!!data.has_more);
        const count = data.total ?? (data.items || []).length;
        const dims = {
          listing_type: listingType,
          property_type: propType !== ANY ? propType : "any",
          sale_type: (listingType === "For Sale" && saleType !== ANY) ? saleType : "any",
          has_location: !!(loc.code || loc.name),
          has_developer: !!dev.id,
          has_project: !!proj.id,
          has_price_filter: !!(minPrice || maxPrice),
          result_count: count,
        };
        trackEvent("marketplace_search", { entity_type: "search", metadata: dims });
        if (count === 0) trackEvent("marketplace_zero_results", { entity_type: "search", metadata: dims });
      }
    } catch {
      if (seq === reqSeq.current) { setItems([]); setTotal(0); setHasMore(false); }
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    try {
      const { data } = await api.get("/public/listings", { params: { ...buildListingParams(), page: nextPage, page_size: PAGE_SIZE } });
      pageRef.current = nextPage;
      setItems((prev) => [...(prev || []), ...(data.items || [])]);
      setHasMore(!!data.has_more);
    } catch { /* keep existing list on failure */ } finally { setLoadingMore(false); }
  };

  const loadSaved = async () => {
    if (!isConsumer) { setSavedIds(new Set()); return; }
    try {
      const { data } = await api.get("/me/saved-properties");
      setSavedIds(new Set(data.map((d) => d.id)));
    } catch { /* keep current */ }
  };

  // reload whenever the Buy/Rent/Stays tab changes; other filters apply via the Search button
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [listingType]);
  // keep the active tab in sync with ?type when navigated in-app (e.g. public header Buy/Rent/Stays)
  useEffect(() => {
    const t = sp.get("type");
    const next = ["For Rent", "Unit Hosting"].includes(t) ? t : "For Sale";
    setListingType((prev) => (prev === next ? prev : next));
    /* eslint-disable-next-line */
  }, [sp]);
  useEffect(() => { loadSaved(); /* eslint-disable-next-line */ }, [user]);
  // Reload properties after chip removal / clear-all commits fresh filter state (avoids stale-closure load()).
  useEffect(() => { if (propReloadKey > 0) load(); /* eslint-disable-next-line */ }, [propReloadKey]);

  const toggleSave = async (pid) => {
    if (!user) {
      // Preserve Marketplace context: return here after sign-in, and offer create-account.
      toast("Sign in to save properties", { description: "Create a free account to keep your shortlist.", action: { label: "Sign in", onClick: () => nav("/login", { state: { from: locp.pathname + locp.search } }) } });
      return;
    }
    if (!isConsumer) return;
    const wasSaved = savedIds.has(pid);
    try {
      if (wasSaved) {
        await api.delete(`/me/saved-properties/${pid}`);
        setSavedIds((s) => { const n = new Set(s); n.delete(pid); return n; });
        toast.success("Removed from saved");
      } else {
        await api.post("/me/saved-properties", { property_id: pid });
        setSavedIds((s) => new Set(s).add(pid));
        toast.success("Saved property");
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const hasActiveFilters = !!(loc.code || loc.name || propType !== ANY || minPrice || maxPrice || dev.id || proj.id);
  const saveSearch = async () => {
    try {
      await api.post("/me/saved-searches", {
        listing_type: listingType,
        location_code: loc.code || null,
        city: loc.name || null,
        property_type: propType !== ANY ? propType : null,
        min_price: minPrice ? Number(minPrice) : null,
        max_price: maxPrice ? Number(maxPrice) : null,
      });
      toast.success("Search saved to your home");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setTab = (t) => {
    setListingType(t);
    const next = new URLSearchParams(sp); next.set("type", t); setSp(next, { replace: true });
  };

  // Developer / Project facet filters — URL-persisted (stable canonical IDs + display name).
  const syncFacetUrl = (nextDev, nextProj) => {
    const next = new URLSearchParams(sp);
    if (nextDev.id) { next.set("developer_id", nextDev.id); next.set("developer", nextDev.name || ""); }
    else { next.delete("developer_id"); next.delete("developer"); }
    if (nextProj.id) { next.set("project_id", nextProj.id); next.set("project", nextProj.name || ""); }
    else { next.delete("project_id"); next.delete("project"); }
    setSp(next, { replace: true });
  };
  const onPickDev = (v) => {
    // Changing developer resets a now-inconsistent project selection (narrowing).
    const nextProj = { id: null, name: null };
    setDev(v); setProj(nextProj); syncFacetUrl(v, nextProj);
  };
  const onPickProj = (v) => { setProj(v); syncFacetUrl(dev, v); };

  // Reload when a facet filter changes (skip the initial mount; listingType effect covers first load).
  useEffect(() => {
    if (!facetInited.current) { facetInited.current = true; return; }
    load();
    /* eslint-disable-next-line */
  }, [dev.id, proj.id, saleType]);

  // Sort is server-side + deterministic; reload on change (skip first mount).
  const sortInited = useRef(false);
  const changeSort = (v) => {
    setSort(v);
    const next = new URLSearchParams(sp); if (v && v !== "newest") next.set("sort", v); else next.delete("sort"); setSp(next, { replace: true });
  };
  useEffect(() => {
    if (!sortInited.current) { sortInited.current = true; return; }
    load();
    /* eslint-disable-next-line */
  }, [sort]);

  // Active-filter chips — single source of truth derived from state (no duplicate chip state).
  const activeChips = [];
  if (loc.code || loc.name) activeChips.push({ k: "loc", label: loc.name || "Location", onRemove: () => { setLoc({ code: "", name: "" }); const n = new URLSearchParams(sp); n.delete("location_code"); n.delete("loc"); setSp(n, { replace: true }); setPropReloadKey((k) => k + 1); } });
  if (propType !== ANY) activeChips.push({ k: "ptype", label: propertyTypeLabel(propType), onRemove: () => { setPropType(ANY); setPropReloadKey((k) => k + 1); } });
  if (minPrice || maxPrice) activeChips.push({ k: "price", label: `${minPrice ? "₱" + Number(minPrice).toLocaleString() : "Any"}–${maxPrice ? "₱" + Number(maxPrice).toLocaleString() : "Any"}`, onRemove: () => { setMinPrice(""); setMaxPrice(""); setPropReloadKey((k) => k + 1); } });
  if (dev.id) activeChips.push({ k: "dev", label: dev.name || "Developer", onRemove: () => onPickDev({ id: null, name: null }) });
  if (proj.id) activeChips.push({ k: "proj", label: proj.name || "Project", onRemove: () => onPickProj({ id: null, name: null }) });
  if (listingType === "For Sale" && saleType !== ANY) activeChips.push({ k: "sale", label: (SALE_TYPES.find((s) => s.value === saleType)?.shortLabel) || saleType, onRemove: () => pickSaleType(ANY) });
  const clearAll = () => {
    setLoc({ code: "", name: "" }); setPropType(ANY); setMinPrice(""); setMaxPrice(""); setGuests("");
    setDev({ id: null, name: null }); setProj({ id: null, name: null }); setSaleType(ANY);
    const n = new URLSearchParams(); if (listingType !== "For Sale") n.set("type", listingType); setSp(n, { replace: true });
    setPropReloadKey((k) => k + 1);
  };

  const pickSaleType = (v) => {
    setSaleType(v);
    const next = new URLSearchParams(sp);
    if (v && v !== ANY) next.set("sale_type", v); else next.delete("sale_type");
    setSp(next, { replace: true });
  };

  // Buyer Needs — active-filter chips, clear all, and client-side sort (shared Marketplace UX).
  const bnReload = () => setBnReloadKey((k) => k + 1);
  // Authenticated users browse Buyer Needs on the dedicated /buyer-needs page.
  // Redirect any internal mode=buyer-needs deep-link to it (public/guests keep the gate).
  useEffect(() => { if (user && mode === "buyer-needs") nav("/buyer-needs", { replace: true }); }, [user, mode, nav]);

  const bnChips = [];
  if (bnLoc.code || bnLoc.name) bnChips.push({ k: "loc", label: bnLoc.name || "Location", onRemove: () => { setBnLoc({ code: "", name: "" }); bnReload(); } });
  if (bnListingType !== ANY) bnChips.push({ k: "txn", label: bnListingType === "Unit Hosting" ? "Stays" : bnListingType, onRemove: () => { setBnListingType(ANY); bnReload(); } });
  if (bnPropType !== ANY) bnChips.push({ k: "ptype", label: propertyTypeLabel(bnPropType), onRemove: () => { setBnPropType(ANY); bnReload(); } });
  if (bnMinBudget || bnMaxBudget) bnChips.push({ k: "budget", label: `${bnMinBudget ? peso(bnMinBudget) : "Any"}–${bnMaxBudget ? peso(bnMaxBudget) : "Any"}`, onRemove: () => { setBnMinBudget(""); setBnMaxBudget(""); bnReload(); } });
  if (bnMinNightly || bnMaxNightly) bnChips.push({ k: "nightly", label: `${bnMinNightly ? peso(bnMinNightly) : "Any"}–${bnMaxNightly ? peso(bnMaxNightly) : "Any"} /night`, onRemove: () => { setBnMinNightly(""); setBnMaxNightly(""); bnReload(); } });
  if (bnBedrooms !== ANY) bnChips.push({ k: "beds", label: `${bnBedrooms}+ beds`, onRemove: () => { setBnBedrooms(ANY); bnReload(); } });
  if (bnGuests !== ANY) bnChips.push({ k: "guests", label: `${bnGuests}+ guests`, onRemove: () => { setBnGuests(ANY); bnReload(); } });
  if (bnMinBeds !== ANY) bnChips.push({ k: "minbeds", label: `${bnMinBeds}+ beds`, onRemove: () => { setBnMinBeds(ANY); bnReload(); } });
  const bnClearAll = () => {
    setBnLoc({ code: "", name: "" }); setBnListingType(ANY); setBnPropType(ANY);
    setBnMinBudget(""); setBnMaxBudget(""); setBnMinNightly(""); setBnMaxNightly("");
    setBnBedrooms(ANY); setBnGuests(ANY); setBnMinBeds(ANY); bnReload();
  };
  const bnBudgetVal = (n) => n.listing_type === "Unit Hosting" ? (n.nightly_budget_max || n.nightly_budget_min || 0) : (n.budget_max || n.budget_min || 0);
  const bnSorted = bnItems ? [...bnItems].sort((a, b) => {
    if (bnSort === "budget_desc") return bnBudgetVal(b) - bnBudgetVal(a);
    if (bnSort === "updated") return String(b.updated_at || "").localeCompare(String(a.updated_at || ""));
    return String(b.created_at || "").localeCompare(String(a.created_at || "")); // newest
  }) : bnItems;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketplace-page">
      <Seo
        title="Property Marketplace — PropertyConnect"
        description="Browse active property listings across the Philippines and connect with verified real-estate professionals on PropertyConnect. Connect. Match. Collaborate. Close."
        canonical="/marketplace"
        robots="index,follow"
        jsonLd={organizationJsonLd()}
      />
      {user ? <Navbar /> : <PublicHeader />}

      <section className="bg-[#0F2C59] text-white">
        <div className={`max-w-[1400px] mx-auto px-4 sm:px-6 ${user ? "pt-4 pb-10" : "pt-5 pb-10"}`}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-8">
            <div className="min-w-0">
              <p className="text-[#C79A3B] text-[11px] font-semibold uppercase tracking-[0.2em]">PropertyConnect Marketplace</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">{user ? "Discover Real Estate Opportunities" : "Discover Real Estate Opportunities in the Philippines"}</h1>
              <p className={`text-white/60 text-[13px] mt-1 max-w-xl ${user ? "hidden" : "hidden sm:block"}`}>{user ? "Explore properties, buyer needs, events and professional opportunities across the PropertyConnect network." : "Explore properties, buyer needs, events and professional opportunities across the Philippines."}</p>
            </div>

            {/* LEVEL 1 — Premium primary opportunity channels (right side on desktop, scroll rail on mobile) */}
            <div ref={categoryRailRef} className="w-full lg:w-auto lg:shrink-0 -mx-4 px-4 lg:mx-0 lg:px-0 overflow-x-auto no-scrollbar" data-testid="market-category-rail">
              <div className="flex gap-2.5 w-max lg:w-auto" role="tablist" aria-label="Marketplace categories">
                {[
                  { key: "properties", label: "Properties", sub: "Buy · Rent · Stays", Icon: Building2, testid: "mode-properties", enabled: true },
                  { key: "buyer-needs", label: "Buyer Needs", sub: "Find & Match", Icon: Users, testid: "mode-buyer-needs", enabled: !user },
                  { key: "events", label: "Events", sub: "Open Houses & More", Icon: CalendarDays, testid: "mode-events", enabled: true },
                  { key: "recruitment", label: "Recruitment", sub: "Join Opportunities", Icon: Briefcase, testid: "mode-recruitment", enabled: recruitmentEnabled },
                ].filter((c) => c.enabled).map((c) => {
                  const active = mode === c.key;
                  return (
                    <button
                      key={c.key}
                      ref={active ? activeCategoryRef : null}
                      data-testid={c.testid}
                      role="tab"
                      aria-selected={active}
                      onClick={() => switchMode(c.key)}
                      className={`group relative shrink-0 flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 min-w-[136px] text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B] ${active ? "bg-white/[0.14] border-[#C79A3B] shadow-[0_2px_14px_rgba(199,154,59,0.18)]" : "bg-white/[0.06] border-white/15 hover:bg-white/[0.1] hover:border-white/30"}`}
                    >
                      <span className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 transition-colors ${active ? "bg-[#C79A3B] text-[#0F2C59]" : "bg-white/10 text-white/80 group-hover:text-white"}`}>
                        <c.Icon className="h-[18px] w-[18px]" />
                      </span>
                      <span className="min-w-0">
                        <span className={`block text-sm font-bold leading-tight ${active ? "text-white" : "text-white/90"}`}>{c.label}</span>
                        <span className="block text-[11px] leading-tight mt-0.5 text-white/55 whitespace-nowrap">{c.sub}</span>
                      </span>
                      {active && <span aria-hidden className="absolute left-3.5 right-3.5 -bottom-px h-0.5 rounded-full bg-[#C79A3B]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* LEVEL 2 — Property-mode selector: compact tiles, subordinate to the primary category tiles */}
          {mode === "properties" && (
          <div className="mt-4 flex items-stretch gap-2 max-w-[360px] sm:max-w-[430px]" role="tablist" aria-label="Property browse modes" data-testid="market-listing-type-row">
            {[
              { label: "Buy", val: "For Sale", testid: "market-buy-toggle", Icon: Home },
              { label: "Rent", val: "For Rent", testid: "market-rent-toggle", Icon: KeyRound },
              { label: "Stays", val: "Unit Hosting", testid: "market-stays-toggle", Icon: BedDouble },
            ].map((t) => {
              const active = listingType === t.val;
              return (
                <button
                  key={t.val}
                  data-testid={t.testid}
                  role="tab"
                  aria-pressed={active}
                  aria-selected={active}
                  onClick={() => setTab(t.val)}
                  className={`group relative flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-2 min-h-[40px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B] ${active ? "bg-white/[0.14] border-[#C79A3B]" : "bg-white/[0.05] border-white/15 hover:bg-white/[0.1] hover:border-white/30"}`}
                >
                  <t.Icon className={`h-4 w-4 shrink-0 transition-colors ${active ? "text-[#C79A3B]" : "text-white/80 group-hover:text-white"}`} strokeWidth={2} />
                  <span className={`text-xs font-bold uppercase tracking-wide leading-none transition-colors ${active ? "text-[#C79A3B]" : "text-white/85 group-hover:text-white"}`}>{t.label}</span>
                  {active && <span aria-hidden className="absolute left-2.5 right-2.5 -bottom-px h-0.5 rounded-full bg-[#C79A3B]" />}
                </button>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {mode === "properties" && (<>
      {/* Filters */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="lg:col-span-2">
            <label className="text-xs font-semibold text-slate-500">Location</label>
            <div className="mt-1.5"><LocationFilter value={loc} onPick={setLoc} /></div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Property type</label>
            <Select value={propType} onValueChange={setPropType}>
              <SelectTrigger data-testid="market-type-select" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any type</SelectItem>
                {PROPERTY_TYPE_GROUPS.map((g) => (<SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.items.map((t) => <SelectItem key={t} value={t}>{propertyTypeLabel(t)}</SelectItem>)}</SelectGroup>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">{hosting ? "Min nightly (₱)" : "Min price (₱)"}</label>
            <CurrencyInput data-testid="market-min-price" value={minPrice} onValueChange={setMinPrice} placeholder="Min price" className="mt-1.5" />
          </div>
          {hosting && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Guests</label>
              <Select value={guests || ANY} onValueChange={(v) => setGuests(v === ANY ? "" : v)}>
                <SelectTrigger data-testid="market-guests" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Any</SelectItem>{["1", "2", "3", "4", "5", "6", "8"].map((t) => <SelectItem key={t} value={t}>{t}+</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-500">{hosting ? "Max nightly (₱)" : "Max price (₱)"}</label>
            <div className="flex gap-2 mt-1.5">
              <CurrencyInput data-testid="market-max-price" value={maxPrice} onValueChange={setMaxPrice} placeholder="Max price" />
              <Button data-testid="market-apply" onClick={load} className="h-11 bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white rounded-xl font-semibold shrink-0">Search</Button>
            </div>
          </div>
        </div>
        {/* Developer / Project — primary discovery dimensions (canonical facets, reused dataset) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3" data-testid="market-dev-project-filters">
          <div>
            <label className="text-xs font-semibold text-slate-500">Developer</label>
            <MarketFacetPicker kind="developer" testid="market-developer" listingType={listingType} value={dev} onChange={onPickDev} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Project / Development</label>
            <MarketFacetPicker kind="project" testid="market-project" listingType={listingType} developerId={dev.id} value={proj} onChange={onPickProj} />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button type="button" data-testid="market-all-filters-toggle" onClick={() => setShowAllFilters((v) => !v)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59] hover:text-[#C79A3B] transition-colors">
            <SlidersHorizontal className="h-4 w-4" /> All filters {(listingType === "For Sale" && saleType !== ANY) ? <span className="ml-0.5 rounded-full bg-[#0F2C59] text-white text-[10px] px-1.5 py-0.5">1</span> : null}
            <ChevronDown className={`h-4 w-4 transition-transform ${showAllFilters ? "rotate-180" : ""}`} />
          </button>
        </div>
        {showAllFilters && (
        <div data-testid="market-secondary-filters">
        {/* Sale Type — For Sale only; chips (Any = no filter, includes legacy). Factual discovery segments. */}
        {listingType === "For Sale" && (
          <div className="mt-3 pt-3 border-t border-slate-100" data-testid="market-sale-type-filters">
            <label className="text-xs font-semibold text-slate-500">Sale type</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {[{ value: ANY, shortLabel: "Any" }, ...SALE_TYPES].map((s) => {
                const active = saleType === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`market-sale-type-${s.value === ANY ? "any" : s.value}`}
                    aria-pressed={active}
                    onClick={() => pickSaleType(s.value)}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0F2C59] hover:text-[#0F2C59]"}`}
                  >
                    {s.shortLabel}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </div>
        )}
        {isConsumer && hasActiveFilters && (
          <div className="mt-3 flex justify-end">
            <Button data-testid="market-save-search" onClick={saveSearch} variant="outline" className="h-10 rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold">
              <Bookmark className="h-4 w-4 mr-1.5" /> Save this search
            </Button>
          </div>
        )}
      </div>
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 mt-4 flex flex-wrap items-center gap-2" data-testid="market-active-filters">
          {activeChips.map((c) => (
            <button key={c.k} data-testid={`market-chip-${c.k}`} onClick={c.onRemove} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 pl-3 pr-2 py-1 text-sm text-slate-700 hover:border-[#0F2C59] transition-colors max-w-[240px]">
              <span className="truncate">{c.label}</span><X className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            </button>
          ))}
          {activeChips.length > 1 && <button data-testid="market-clear-all" onClick={clearAll} className="text-sm font-semibold text-[#0F2C59] hover:text-[#C79A3B] transition-colors ml-1">Clear all</button>}
        </div>
      )}

      {/* Premium Spotlight (below filters, above results) — separate internal vs public campaign pools */}
      {mode === "properties" && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-2 space-y-4" data-testid="marketplace-top-spotlight">
          <AdPlacement placement={isPro ? "MARKETPLACE_TOP_INTERNAL" : "MARKETPLACE_TOP_PUBLIC"} variant="grid" />
          <AdPlacement placement="MARKETPLACE_DEVELOPER_STRIP" variant="developer" />
        </div>
      )}
      {/* P0.3 Marketplace Hero Spotlight (separate sponsored module; organic untouched) */}
      {mode === "properties" && <MarketplaceHero params={sponsoredParams} />}

      {/* P0.3 Featured Property module — SEPARATE sponsored row; organic query/pagination untouched */}
      {mode === "properties" && (
        <FeaturedRow params={sponsoredParams}
          renderCard={(p) => <ListingCard p={p} showSave={showSave} saved={savedIds.has(p.id)} onToggleSave={toggleSave} onShare={setShareItem} />} />
      )}

      {/* Results */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {loading || items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (<>
          <div data-testid="marketplace-empty" className="text-center py-20">
            <Building2 className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3 text-sm">No public listings match your filters yet. Try adjusting the location or price.</p>
          </div>
          {proSpotlight}
        </>) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-sm text-slate-500" data-testid="market-result-count">{total} propert{total === 1 ? "y" : "ies"} available</p>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-400 hidden sm:inline">Sort by</span>
                <Select value={sort} onValueChange={changeSort}>
                  <SelectTrigger data-testid="market-sort" className="h-9 w-[170px] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Organic results; the professional spotlight sits after the first rows, never inside the grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="market-results-grid">
              {items.slice(0, SPOTLIGHT_AFTER).map((p) => <ListingCard key={p.id} p={p} showSave={showSave} saved={savedIds.has(p.id)} onToggleSave={toggleSave} onShare={setShareItem} />)}
            </div>
            {proSpotlight}
            {items.length > SPOTLIGHT_AFTER && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="market-results-grid-rest">
                {items.slice(SPOTLIGHT_AFTER).map((p) => <ListingCard key={p.id} p={p} showSave={showSave} saved={savedIds.has(p.id)} onToggleSave={toggleSave} onShare={setShareItem} />)}
              </div>
            )}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button data-testid="market-load-more" onClick={loadMore} disabled={loadingMore}
                  variant="outline" className="px-8 py-2.5 rounded-xl border-[#0F2C59]/20 text-[#0F2C59] font-semibold hover:bg-[#0F2C59]/5">
                  {loadingMore ? "Loading…" : `Load more (${items.length} of ${total})`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      </>)}

      {mode === "buyer-needs" && !isPro && <BuyerNeedsGate />}
      {/* Authenticated Buyer Needs browsing moved to the dedicated /buyer-needs page (redirect above). */}
      {false && isPro && (<>
      {/* Buyer Needs filters */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            <div className="lg:col-span-2">
              <label className="text-xs font-semibold text-slate-500">Location</label>
              <div className="mt-1.5"><LocationFilter value={bnLoc} onPick={setBnLoc} /></div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Transaction</label>
              <Select value={bnListingType} onValueChange={setBnListingType}>
                <SelectTrigger data-testid="bn-listing-type" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Any</SelectItem><SelectItem value="For Sale">For Sale</SelectItem><SelectItem value="For Rent">For Rent</SelectItem><SelectItem value="Unit Hosting">Stays</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Property type</label>
              <Select value={bnPropType} onValueChange={setBnPropType}>
                <SelectTrigger data-testid="bn-type-select" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Any type</SelectItem>{PROPERTY_TYPE_GROUPS.map((g) => (<SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.items.map((t) => <SelectItem key={t} value={t}>{propertyTypeLabel(t)}</SelectItem>)}</SelectGroup>))}</SelectContent>
              </Select>
            </div>
            {bnIsStay ? (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Min nightly (₱)</label>
                  <CurrencyInput data-testid="bn-min-nightly" value={bnMinNightly} onValueChange={setBnMinNightly} placeholder="0" className="mt-1.5" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Max nightly (₱)</label>
                  <CurrencyInput data-testid="bn-max-nightly" value={bnMaxNightly} onValueChange={setBnMaxNightly} placeholder="Any" className="mt-1.5" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Min budget (₱)</label>
                  <CurrencyInput data-testid="bn-min-budget" value={bnMinBudget} onValueChange={setBnMinBudget} placeholder="0" className="mt-1.5" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Max budget (₱)</label>
                  <CurrencyInput data-testid="bn-max-budget" value={bnMaxBudget} onValueChange={setBnMaxBudget} placeholder="Any" className="mt-1.5" />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
            {bnIsStay ? (
              <div className="flex flex-wrap gap-3">
                <div className="w-40">
                  <label className="text-xs font-semibold text-slate-500">Guests</label>
                  <Select value={bnGuests} onValueChange={setBnGuests}>
                    <SelectTrigger data-testid="bn-guests" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent><SelectItem value={ANY}>Any</SelectItem>{["1", "2", "3", "4", "5", "6", "8"].map((t) => <SelectItem key={t} value={t}>{t}+</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <label className="text-xs font-semibold text-slate-500">Min beds</label>
                  <Select value={bnMinBeds} onValueChange={setBnMinBeds}>
                    <SelectTrigger data-testid="bn-min-beds" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent><SelectItem value={ANY}>Any</SelectItem>{["1", "2", "3", "4"].map((t) => <SelectItem key={t} value={t}>{t}+</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="w-40">
                <label className="text-xs font-semibold text-slate-500">Min bedrooms</label>
                <Select value={bnBedrooms} onValueChange={setBnBedrooms}>
                  <SelectTrigger data-testid="bn-bedrooms" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent><SelectItem value={ANY}>Any</SelectItem>{["1", "2", "3", "4", "5"].map((t) => <SelectItem key={t} value={t}>{t}+</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="bn-apply" onClick={loadBuyerNeeds} className="h-11 px-8 bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white rounded-xl font-semibold">Search Buyer Needs</Button>
          </div>
        </div>
      </div>

      {/* Buyer Needs active filter chips */}
      {bnChips.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 mt-4 flex flex-wrap items-center gap-2" data-testid="bn-active-filters">
          {bnChips.map((c) => (
            <button key={c.k} data-testid={`bn-chip-${c.k}`} onClick={c.onRemove} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 pl-3 pr-2 py-1 text-sm text-slate-700 hover:border-[#0F2C59] transition-colors max-w-[240px]">
              <span className="truncate">{c.label}</span><X className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            </button>
          ))}
          {bnChips.length > 1 && <button data-testid="bn-clear-all" onClick={bnClearAll} className="text-sm font-semibold text-[#0F2C59] hover:text-[#C79A3B] transition-colors ml-1">Clear all</button>}
        </div>
      )}

      {/* Buyer Needs results */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        {bnLoading || bnItems === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : bnItems.length === 0 ? (
          <div data-testid="buyer-needs-empty" className="text-center py-20">
            <Users className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3 text-sm">No public Buyer Needs match your filters yet.</p>
            {verifiedPro && (
              <Button data-testid="bn-post-empty" onClick={() => nav("/have-buyer")} className="mt-5 h-10 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Buyer Need</Button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <p className="text-sm text-slate-500" data-testid="bn-result-count">{bnTotal} buyer need{bnTotal === 1 ? "" : "s"} looking for a match</p>
              <div className="flex items-center gap-2 shrink-0">
                {verifiedPro && (
                  <Button data-testid="bn-post" onClick={() => nav("/have-buyer")} variant="outline" className="h-9 rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Buyer Need</Button>
                )}
                <span className="text-xs text-slate-400 hidden sm:inline">Sort by</span>
                <Select value={bnSort} onValueChange={setBnSort}>
                  <SelectTrigger data-testid="bn-sort" className="h-9 w-[170px] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="updated">Recently Updated</SelectItem>
                    <SelectItem value="budget_desc">Budget: High to Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {bnSorted.map((n) => <BuyerNeedCard key={n.id} n={n} />)}
            </div>
            {bnHasMore && (
              <div className="flex justify-center mt-8">
                <Button data-testid="bn-load-more" onClick={loadMoreBuyerNeeds} disabled={bnLoadingMore}
                  variant="outline" className="px-8 py-2.5 rounded-xl border-[#0F2C59]/20 text-[#0F2C59] font-semibold hover:bg-[#0F2C59]/5">
                  {bnLoadingMore ? "Loading…" : `Load more (${bnItems.length} of ${bnTotal})`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      </>)}
      {mode === "events" && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="events-section">
          <PromotedEventsRow />
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900">Real Estate Events</h2>
              <p className="text-sm text-slate-500 mt-0.5">Open houses, launches, briefings and networking near you.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {verifiedPro && <Button data-testid="events-post" onClick={() => nav("/post-event")} variant="outline" className="h-9 rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Event</Button>}
              <Select value={evType} onValueChange={setEvType}>
                <SelectTrigger data-testid="events-type-filter" className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>All types</SelectItem>
                  <SelectItem value="OPEN_HOUSE">Open House</SelectItem>
                  <SelectItem value="PROJECT_PREVIEW">Project Preview</SelectItem>
                  <SelectItem value="PROJECT_BRIEFING">Project Briefing</SelectItem>
                  <SelectItem value="PROJECT_LAUNCH">Project Launch</SelectItem>
                  <SelectItem value="INVESTORS_NIGHT">Investors' Night</SelectItem>
                  <SelectItem value="ACCREDITATION">Accreditation</SelectItem>
                  <SelectItem value="PROPERTY_VIEWING">Property Viewing</SelectItem>
                  <SelectItem value="SEMINAR_TRAINING">Seminar / Training</SelectItem>
                  <SelectItem value="NETWORKING">Networking</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {evLoading || evItems === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : evItems.length === 0 ? (
            <div data-testid="events-empty" className="text-center py-20">
              <CalendarDays className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3 text-sm">No upcoming events yet.</p>
              {verifiedPro && <Button data-testid="events-post-empty" onClick={() => nav("/post-event")} className="mt-5 h-10 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Event</Button>}
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-4" data-testid="events-count">{evItems.length} upcoming event{evItems.length === 1 ? "" : "s"}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {evItems.map((ev) => <EventCard key={ev.id} ev={ev} />)}
              </div>
            </>
          )}
        </div>
      )}
      {mode === "recruitment" && recruitmentEnabled && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="recruitment-section">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900">Recruitment Opportunities</h2>
              <p className="text-sm text-slate-500 mt-0.5">Discover career, team-building and professional opportunities across the PropertyConnect network.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {verifiedPro && <Button data-testid="recruitment-post" onClick={() => nav("/post-recruitment")} variant="outline" className="h-9 rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Recruitment</Button>}
              <Select value={rcType} onValueChange={setRcType}>
                <SelectTrigger data-testid="recruitment-type-filter" className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>All types</SelectItem>
                  <SelectItem value="FULL_TIME">Full-time</SelectItem>
                  <SelectItem value="PART_TIME">Part-time</SelectItem>
                  <SelectItem value="COMMISSION">Commission-based</SelectItem>
                  <SelectItem value="CONTRACT">Contract</SelectItem>
                  <SelectItem value="INTERNSHIP">Internship</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {rcLoading || rcItems === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : rcItems.length === 0 ? (
            <div data-testid="recruitment-empty" className="text-center py-20">
              <Briefcase className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3 text-sm">No open opportunities yet.</p>
              {verifiedPro && <Button data-testid="recruitment-post-empty" onClick={() => nav("/post-recruitment")} className="mt-5 h-10 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Recruitment</Button>}
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-4" data-testid="recruitment-count">{rcItems.length} open opportunit{rcItems.length === 1 ? "y" : "ies"}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rcItems.map((r) => <RecruitmentCard key={r.id} r={r} />)}
              </div>
            </>
          )}
        </div>
      )}
      {shareItem && (
        <ShareDialog
          open={!!shareItem}
          onOpenChange={(v) => { if (!v) setShareItem(null); }}
          url={listingShareUrl(shareItem.id)}
          title={`For Sale — ${shareItem.title}`}
          text={`${shareItem.title} — ${shareItem.city || "Philippines"}. View this property on PropertyConnect.`}
          entityId={shareItem.id}
          heading="Share this property"
          subtitle="Share this PropertyConnect listing with buyers and your professional network."
        />
      )}
      <SiteFooter variant={user ? "compact" : "full"} />
    </div>
  );
}
